import { createRef, useCallback, useEffect, useState } from 'react'
import { Container, Loader, Pagination, Ref } from 'semantic-ui-react'
import { Text } from '@/componentsV1/BaseComponents'
import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { useDebounce, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '../../../utils/typeHelpers'
import { useOffsetPaginatedQuery } from '../paginated-table/useOffsetPaginatedQuery'
import {
  fetchAdminAllQuarterlyTaxEstimateDetailsIfNeeded,
  FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
} from '../quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectActiveQuarterlyTaxEstimateDetails } from '../quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import CalculationDateToolbar from './components/CalculationDateToolbar'
import QTEDateDetails from './components/QTEDateDetails'
import CalculationTabMenu from './components/CalculationTabMenu'
import CalculationTable from './components/CalculationTable'
import {
  adminFetchUserTaxCalculations,
  PaginatedCalculationsResponse,
  SortPreset,
  TabCounts,
  USER_TAX_CALCULATION_TAB_TO_API_PARAM,
  UserTaxCalculationTabNames,
} from './userTaxCalculation.slice'

const PAGE_SIZE = 25

const EMPTY_COUNTS: TabCounts = {
  all: 0,
  singleState: 0,
  multiState: 0,
  edgeCases: 0,
}

const QuarterlyTaxCalculationsPanel = () => {
  const dispatch = useAppDispatch()
  const [calendarYear, setCalendarYear] = useState('')
  const [calendarQuarter, setCalendarQuarter] = useState('')
  const [activeTab, setActiveTab] = useState(UserTaxCalculationTabNames.all)
  const [userSearch, setUserSearch] = useState('')
  const [readyToCalculateFilter, setReadyToCalculateFilter] = useState(true)
  const [sortBy, setSortBy] = useState<SortPreset>('default')
  const debouncedUserSearch = useDebounce(userSearch, 300).trim()

  const contextRef = createRef<HTMLElement>()

  useEffect(() => {
    dispatch(fetchAdminAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  const activeQuarterlyTaxEstimateDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const isFetching = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY
  )
  const fetchError = useReselector(
    getFetchError,
    FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY
  )

  useEffect(() => {
    if (
      !calendarYear &&
      !calendarQuarter &&
      activeQuarterlyTaxEstimateDetails
    ) {
      setCalendarYear(activeQuarterlyTaxEstimateDetails.taxYear)
      setCalendarQuarter(activeQuarterlyTaxEstimateDetails.taxQuarter)
    }
  }, [activeQuarterlyTaxEstimateDetails, calendarQuarter, calendarYear])

  const fetchData = useCallback(
    (pagination: { skip: number; take: number }) => {
      if (!calendarYear || !calendarQuarter) {
        return Promise.resolve({
          data: [],
          pagination: {
            totalItems: 0,
            ...pagination,
          },
          counts: EMPTY_COUNTS,
        } satisfies PaginatedCalculationsResponse)
      }

      return dispatch(
        adminFetchUserTaxCalculations(pagination, {
          calendarYear,
          calendarQuarter,
          tab: USER_TAX_CALCULATION_TAB_TO_API_PARAM[activeTab],
          sortBy,
          userSearch: debouncedUserSearch || undefined,
          readyToCalculate: readyToCalculateFilter ? true : undefined,
        })
      )
    },
    [
      activeTab,
      calendarQuarter,
      calendarYear,
      debouncedUserSearch,
      dispatch,
      readyToCalculateFilter,
      sortBy,
    ]
  )

  const { response, loading, error, activePage, totalPages, handlePageChange } =
    useOffsetPaginatedQuery<PaginatedCalculationsResponse>({
      fetchData,
      take: PAGE_SIZE,
    })

  const onDateChange = useCallback(
    ({
      selectedQuarter,
      selectedYear,
    }: {
      selectedQuarter: string
      selectedYear: string
    }) => {
      setCalendarYear(selectedYear)
      setCalendarQuarter(selectedQuarter)

      setActiveTab(UserTaxCalculationTabNames.all)
      setSortBy('default')
    },
    []
  )

  const onTabChange = useCallback(
    (tabName: UserTaxCalculationTabNames) => {
      setActiveTab(tabName)
    },
    [setActiveTab]
  )

  return (
    <Ref innerRef={contextRef}>
      <Container style={{ width: '100%' }} id="userTaxCalculations">
        <Container style={{ width: '100%' }}>
          {fetchError && (
            <p>
              An error occurred fetching quarterly tax estimate details{' '}
              <i>{fetchError.message}</i>
            </p>
          )}
          {error && (
            <p>
              An error occurred fetching tax calculations <i>{error.message}</i>
            </p>
          )}
          {isFetching && <p>Loading quarterly tax estimate details</p>}
          {!isFetching && !fetchError && (
            <>
              <CalculationDateToolbar
                onDateChange={onDateChange}
                selectedYear={calendarYear}
                selectedQuarter={calendarQuarter}
              />
              {calendarQuarter && calendarYear && activeTab && (
                <>
                  <QTEDateDetails
                    calendarQuarter={calendarQuarter}
                    calendarYear={calendarYear}
                    style={{ padding: '1em', maxWidth: '550px' }}
                  />

                  <CalculationTabMenu
                    activeTab={activeTab}
                    contextRef={contextRef}
                    counts={response?.counts ?? EMPTY_COUNTS}
                    onTabChange={onTabChange}
                  />
                  {loading ? (
                    <Text
                      as="h1"
                      textAlign="center"
                      style={{ verticalAlign: 'middle', padding: '1em' }}
                    >
                      Loading tax calculations <Loader active inline />
                    </Text>
                  ) : (
                    <>
                      <CalculationTable
                        calculations={response?.data ?? []}
                        calendarQuarter={calendarQuarter}
                        calendarYear={calendarYear}
                        activeTab={activeTab}
                        userSearch={userSearch}
                        onUserSearchChange={setUserSearch}
                        readyToCalculateFilter={readyToCalculateFilter}
                        onReadyToCalculateChange={setReadyToCalculateFilter}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        filteredResultCount={
                          response?.pagination?.totalItems ?? 0
                        }
                      />
                      {totalPages > 1 && (
                        <div
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Pagination
                            activePage={activePage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Container>
      </Container>
    </Ref>
  )
}

export default QuarterlyTaxCalculationsPanel
